import React, { useState, useEffect, useRef } from 'react';
import { Icon } from '@iconify/react';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import '../../../assets/styles/custom.css';
import countryData from '../../../utils/countory.json';
import useDebounce from '../../../hooks/useDebounce';
import { usePermissions } from '../../../context/PermissionsContext';
import {
  getTraineeDetails,
  activeInactiveTrainee,
  fetchUserDetails,
  deleteTrainee,
  editTraineeDetails,
  createNewOrganisationUser,
  BulkUploadUserDetails,
  getSample,
  bulkUploadUsersJSON
} from '../../../services/traineeService';
import TraineeGroups from '../roles_and_access/TraineeGroups';
import * as XLSX from 'xlsx';
import { useNavigate } from 'react-router-dom';
import { encodeData } from '../../../utils/encodeAndEncode';

function Trainees({ forceTab }) {
  const navigate = useNavigate();
  const { permissions } = usePermissions();
  const [activeTab, setActiveTab] = useState(forceTab || 'trainees'); // 'trainees' | 'groups'

  const isEmbedded = Boolean(forceTab);
  const [showModal, setShowModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [activeFilter, setActiveFilter] = useState('all'); // 'all', 'active', 'inactive'
  const [mode, setMode] = useState('Create'); // 'Create' or 'Edit'
  const [selectedTrainee, setSelectedTrainee] = useState(null);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [trainees, setTrainees] = useState([]);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    totalPages: 0,
    totalCount: 0
  });

  const [formData, setFormData] = useState({
    profileImage: null,
    profile_pic_url: '',
    upload_image_url: null,
    name: '',
    email: '',
    gender: '',
    dateOfBirth: '',
    phoneNumber: '',
    countryCode: '+91',
    employeeNo: '',
    companyCode: '',
    companyDescription: '',
    jobTitle: '',
    dateJoined: '',
    icNo: '',
    finNo: '',
    pwmRank: '',
    password: ''
  });

  const [errors, setErrors] = useState({});
  const [imagePreview, setImagePreview] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const fileInputRef = useRef(null);

  // Bulk upload modal states
  const [showBulkUploadModal, setShowBulkUploadModal] = useState(false);
  const [bulkUploadFile, setBulkUploadFile] = useState(null);
  const [bulkUploadData, setBulkUploadData] = useState([]);
  const [bulkUploadErrors, setBulkUploadErrors] = useState([]);
  const [bulkUploadSuccesses, setBulkUploadSuccesses] = useState([]);
  const [bulkUploadLoading, setBulkUploadLoading] = useState(false);
  const [bulkUploadStep, setBulkUploadStep] = useState('upload'); // 'upload', 'validate', 'submit', 'result'
  const bulkFileInputRef = useRef(null);

  // Debounced search term
  const debouncedSearchTerm = useDebounce(searchTerm, 500);

  // Validation function
  const validateForm = (mode) => {
    const newErrors = {};
    const emailRegex = /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,4}$/;
    const phoneRegex = /^[0-9]+$/;
    const passwordRegex = /^(?=.*[a-zA-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/;

    // Required fields
    if (!formData.name.trim()) newErrors.name = 'Name is required';
    if (formData.name.length > 25) newErrors.name = 'Name must be less than 25 characters';
    if (!formData.email.trim()) newErrors.email = 'Email is required';
    if (formData.email && !emailRegex.test(formData.email)) newErrors.email = 'Invalid email format';
    if (!formData.gender) newErrors.gender = 'Gender is required';
    if (!formData.dateOfBirth) newErrors.dateOfBirth = 'Date of Birth is required';

    // Password validation (required for create, optional for edit)
    if (mode === 'Create' && !formData.password) newErrors.password = 'Password is required';
    if (formData.password && formData.password.length < 8) newErrors.password = 'Password must be at least 8 characters';
    if (formData.password && !passwordRegex.test(formData.password)) {
      newErrors.password = 'Password must contain at least one uppercase letter, one lowercase letter, one number, and one special character';
    }

    // Optional field validations
    if (formData.phoneNumber && !phoneRegex.test(formData.phoneNumber)) {
      newErrors.phoneNumber = 'Phone number must contain only digits';
    }
    if (formData.employeeNo.length > 50) newErrors.employeeNo = 'Employee No must be less than 50 characters';
    if (formData.companyCode.length > 50) newErrors.companyCode = 'Company Code must be less than 50 characters';
    if (formData.companyDescription.length > 255) newErrors.companyDescription = 'Company Description must be less than 255 characters';
    if (formData.pwmRank.length > 1000) newErrors.pwmRank = 'PWM Rank must be less than 1000 characters';
    if (formData.jobTitle.length > 1000) newErrors.jobTitle = 'Job Title must be less than 1000 characters';

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handlePageChange = (nextPage) => {
    if (loading) return;
    setPagination((prev) => {
      const totalPages = Number(prev.totalPages) || 0;
      const clamped = totalPages > 0 ? Math.min(Math.max(1, nextPage), totalPages) : Math.max(1, nextPage);
      return { ...prev, page: clamped };
    });
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setFormData(prev => ({
        ...prev,
        profileImage: file,
        upload_image_url: file
      }));

      // Create preview URL
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  // Fetch trainees data
  const fetchTraineesData = async () => {
    try {
      setLoading(true);
      const status = activeFilter === 'all' ? null : activeFilter === 'active' ? true : false;
      const response = await getTraineeDetails({
        search: debouncedSearchTerm,
        page: pagination.page,
        limit: pagination.limit,
        status: status
      });

      // console.log('Trainees data', response);

      if (response.success) {
        const { data, pagination: paginationData } = response.data;
        setTrainees(data || []);
        setPagination({
          page: paginationData.page,
          limit: paginationData.limit,
          totalPages: paginationData.totalPages,
          totalCount: paginationData.totalCount
        });
      } else {
        toast.error("Failed to fetch trainee data");
        setTrainees([]);
      }
    } catch (error) {
      console.error("Error fetching trainee data:", error);
      toast.error("Something went wrong");
      setTrainees([]);
    } finally {
      setLoading(false);
    }
  };

  // Reset form data
  const resetForm = () => {
    setFormData({
      profileImage: null,
      profile_pic_url: '',
      upload_image_url: null,
      name: '',
      email: '',
      gender: '',
      dateOfBirth: '',
      phoneNumber: '',
      countryCode: '+91',
      employeeNo: '',
      companyCode: '',
      companyDescription: '',
      jobTitle: '',
      dateJoined: '',
      icNo: '',
      finNo: '',
      pwmRank: '',
      password: ''
    });
    setImagePreview(null);
    setErrors({});
  };

  // Handle modal show for create/edit
  const handleShowModal = async (modeType, trainee = null) => {
    if (modeType === 'Create' && !permissions.trainee_management_create) {
      toast.warning("You don't have permission to create trainees");
      return;
    }
    if (modeType === 'Edit' && !permissions.trainee_management_edit) {
      toast.warning("You don't have permission to edit trainees");
      return;
    }
    setMode(modeType);
    setSelectedTrainee(trainee);
    setShowModal(true);

    if (modeType === 'Edit' && trainee) {
      try {
        setLoading(true);
        const response = await fetchUserDetails(trainee.id);

        if (response.success && response.data) {
          // console.log('Full API Response:', response.data);
          const personalInfo = response.data.personal_information || {};
          const employeeDetails = response.data.employee_details || {};
          // console.log('IC No from employeeDetails:', employeeDetails.ic_no);
          // console.log('IC No from personalInfo:', personalInfo.ic_no);
          // console.log('FIN No from employeeDetails:', employeeDetails.fin_no);
          // console.log('FIN No from personalInfo:', personalInfo.fin_no);

          // Helper function to safely get string value and handle Buffer objects
          const getStringValue = (value) => {
            // console.log('Processing value:', value, 'Type:', typeof value);

            if (value === null || value === undefined) return '';

            // If it's already a string, return it
            if (typeof value === 'string') return value;

            // Handle Buffer objects specifically
            if (value && typeof value === 'object') {
              // Check if it's a Buffer object (Node.js style)
              if (value.type === 'Buffer' && Array.isArray(value.data)) {
                try {
                  // Convert Buffer data array to string
                  const result = String.fromCharCode.apply(null, value.data);
                  // console.log('Converted Buffer to string:', result);
                  return result;
                } catch (e) {
                  console.error('Error converting buffer to string:', e);
                  return '';
                }
              }

              // Check if it's a Uint8Array or similar
              if (value instanceof Uint8Array || (value.constructor && value.constructor.name === 'Uint8Array')) {
                try {
                  const result = String.fromCharCode.apply(null, Array.from(value));
                  // console.log('Converted Uint8Array to string:', result);
                  return result;
                } catch (e) {
                  console.error('Error converting Uint8Array to string:', e);
                  return '';
                }
              }

              // Check if it has a data property that might be the actual value
              if (value.data !== undefined) {
                return getStringValue(value.data);
              }

              // For other objects, avoid "[object Object]"
              const stringValue = value.toString();
              if (stringValue === '[object Object]') {
                // console.log('Returning empty string for [object Object]');
                return '';
              }
              return stringValue;
            }

            return String(value);
          };

          setFormData({
            profileImage: null,
            profile_pic_url: response.data.profile_pic || '',
            upload_image_url: null,
            name: getStringValue(personalInfo.name),
            email: getStringValue(personalInfo.email),
            phoneNumber: getStringValue(personalInfo.mobile),
            countryCode: getStringValue(personalInfo.mobilenocode) || '+91',
            employeeNo: getStringValue(employeeDetails.employee_no || personalInfo.employee_no),
            companyCode: getStringValue(employeeDetails.company_code || personalInfo.company_code),
            companyDescription: getStringValue(employeeDetails.company_description || personalInfo.company_description),
            icNo: getStringValue(employeeDetails.ic_no || personalInfo.ic_no),
            finNo: getStringValue(employeeDetails.fin_no || personalInfo.fin_no),
            gender: getStringValue(employeeDetails.gender || personalInfo.gender),
            dateOfBirth: personalInfo.date_of_birth ? personalInfo.date_of_birth.split('T')[0] : '',
            dateJoined: personalInfo.date_joined ? personalInfo.date_joined.split('T')[0] : '',
            pwmRank: getStringValue(employeeDetails.pwm_rank || personalInfo.pwm_rank),
            jobTitle: getStringValue(employeeDetails.job_title || personalInfo.job_title),
            password: ''
          });

          if (response.data.profile_pic) {
            setImagePreview(response.data.profile_pic);
          }
        } else {
          toast.error("Failed to fetch user details");
        }
      } catch (error) {
        console.error("Error fetching user details:", error);
        toast.error("Something went wrong");
      } finally {
        setLoading(false);
      }
    } else {
      resetForm();
    }
  };

  // Handle modal close
  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedTrainee(null);
    resetForm();
    setMode('Create');
  };

  // Handle save (create/edit)
  const handleSave = async () => {
    try {
      if (!validateForm(mode)) {
        return;
      }

      setLoading(true);
      const formDataToSend = new FormData();

      // Append basic fields
      formDataToSend.append("name", formData.name);
      formDataToSend.append("email", formData.email);
      formDataToSend.append("mobileno_code", formData.countryCode);
      formDataToSend.append("mobile", formData.phoneNumber);
      formDataToSend.append("gender", formData.gender);
      formDataToSend.append("date_of_birth", formData.dateOfBirth);

      // Password handling
      if (mode === 'Create' || (mode === 'Edit' && formData.password.trim() !== "")) {
        formDataToSend.append("password", formData.password);
      }

      // Optional fields
      formDataToSend.append("employee_no", formData.employeeNo || "");
      formDataToSend.append("company_code", formData.companyCode || "");
      formDataToSend.append("company_description", formData.companyDescription || "");
      formDataToSend.append("ic_no", formData.icNo || "");
      formDataToSend.append("fin_no", formData.finNo || "");
      formDataToSend.append("date_joined", formData.dateJoined || "");
      formDataToSend.append("pwm_rank", formData.pwmRank || "");
      formDataToSend.append("job_title", formData.jobTitle || "");

      let response;
      if (mode === 'Edit' && selectedTrainee) {
        // Handle image for edit
        if (formData.upload_image_url !== null && formData.upload_image_url !== undefined) {
          formDataToSend.append("profile_pic", formData.upload_image_url);
        }
        response = await editTraineeDetails(selectedTrainee.id, formDataToSend);
      } else {
        // Handle image for create
        if (formData.upload_image_url !== null) {
          formDataToSend.append("profile_pic", formData.upload_image_url);
        } else if (formData.profile_pic_url && formData.profile_pic_url.trim() !== '') {
          formDataToSend.append("profile_pic_url", formData.profile_pic_url);
        }
        response = await createNewOrganisationUser(formDataToSend);
      }

      if (response.success) {
        toast.success(mode === 'Edit' ? "Trainee updated successfully" : "Trainee created successfully");
        handleCloseModal();
        fetchTraineesData();
      } else {
        toast.error("Failed to save trainee data");
      }
    } catch (error) {
      console.error("Error saving trainee:", error);
      toast.error(error.response?.data?.message || "Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  // Handle delete trainee
  const handleDelete = async (trainee) => {
    if (!permissions.trainee_management_delete) {
      toast.warning("You don't have permission to delete trainees");
      return;
    }
    if (window.confirm(`Are you sure you want to delete ${trainee.name}?`)) {
      try {
        setLoading(true);
        const response = await deleteTrainee(trainee.id);

        if (response.success) {
          toast.success("Trainee deleted successfully");
          fetchTraineesData();
        } else {
          toast.error("Failed to delete trainee");
        }
      } catch (error) {
        console.error("Error deleting trainee:", error);
        toast.error("Something went wrong");
      } finally {
        setLoading(false);
      }
    }
  };

  // Handle status toggle
  const handleStatusToggle = async (trainee) => {
    if (!permissions.trainee_management_status) {
      toast.warning("You don't have permission to change trainee status");
      return;
    }
    const traineeId = trainee.id;
    try {
      const newStatus = trainee.is_blocked ? 0 : 1;
      const response = await activeInactiveTrainee(traineeId, newStatus);

      if (response.success) {
        // Update trainee status locally
        setTrainees(prevTrainees => prevTrainees.map(t => 
          t.id === traineeId ? { ...t, is_blocked: newStatus } : t
        ));
        toast.success(`Trainee ${newStatus === 0 ? 'activated' : 'deactivated'} successfully`);
      } else {
        toast.error("Failed to update status");
      }
    } catch (error) {
      console.error("Error updating status:", error);
      toast.error("Something went wrong");
    }
  };

  // Handle bulk upload
  const handleBulkUpload = async (file) => {
    if (!file) return;
    
    // Validate file type
    const validTypes = [
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', // .xlsx
      'application/vnd.ms-excel' // .xls
    ];
    const validExtensions = ['.xlsx', '.xls'];
    
    const fileExtension = '.' + file.name.split('.').pop().toLowerCase();
    
    if (!validTypes.includes(file.type) && !validExtensions.includes(fileExtension)) {
      toast.error('Please select only Excel files (.xlsx or .xls)');
      return;
    }
    
    try {
      setLoading(true);
      // console.log('📄 ========================================');
      // console.log('📄 Starting Excel File Read Process');
      // console.log('📄 File Name:', file.name);
      // console.log('📄 File Size:', (file.size / 1024).toFixed(2), 'KB');
      // console.log('📄 File Type:', file.type);
      // console.log('📄 File Extension:', fileExtension);
      // console.log('📄 ========================================');
      
      // Step 1: Read file as ArrayBuffer
      // console.log('📖 Step 1: Reading file as ArrayBuffer...');
      let arrayBuffer;
      try {
        arrayBuffer = await file.arrayBuffer();
        // console.log('✅ File read successfully. Size:', arrayBuffer.byteLength, 'bytes');
      } catch (readError) {
        console.error('❌ Error reading file:', readError);
        throw new Error(`Failed to read file: ${readError.message}`);
      }
      
      // Step 2: Import xlsx library
      // console.log('📦 Step 2: Loading xlsx library...');
      let XLSX;
      try {
        XLSX = await import('xlsx');
        // console.log('✅ xlsx library loaded successfully');
        // console.log('   Library version:', XLSX.version || 'unknown');
      } catch (importError) {
        console.error('❌ Error importing xlsx library:', importError);
        console.error('💡 Solution: Run "npm install xlsx" in your project directory');
        throw new Error('xlsx library not found. Please install it using: npm install xlsx');
      }
      
      // Step 3: Parse the Excel file
      // console.log('🔍 Step 3: Parsing Excel file...');
      let workbook;
      try {
        workbook = XLSX.read(arrayBuffer, { 
          type: 'array',
          cellDates: true,
          cellNF: false,
          cellText: false
        });
        // console.log('✅ Excel file parsed successfully');
      } catch (parseError) {
        console.error('❌ Error parsing Excel file:', parseError);
        console.error('   Error details:', {
          name: parseError.name,
          message: parseError.message,
          stack: parseError.stack
        });
        throw new Error(`Invalid Excel file format: ${parseError.message}`);
      }
      
      // Step 4: Check if workbook has sheets
      if (!workbook.SheetNames || workbook.SheetNames.length === 0) {
        throw new Error('Excel file contains no sheets');
      }
      
      // console.log('📊 ========================================');
      // console.log('📊 Workbook Information:');
      // console.log('  - Sheet Names:', workbook.SheetNames);
      // console.log('  - Number of Sheets:', workbook.SheetNames.length);
      // console.log('📊 ========================================');
      
      // Step 5: Read data from all sheets
      // console.log('📋 Step 4: Reading data from sheets...');
      const allSheetData = {};
      
      workbook.SheetNames.forEach((sheetName, index) => {
        // console.log(`\n📋 Processing Sheet ${index + 1}: "${sheetName}"`);
        
        try {
          const worksheet = workbook.Sheets[sheetName];
          
          if (!worksheet) {
            console.warn(`⚠️ Sheet "${sheetName}" is empty or invalid`);
            allSheetData[sheetName] = {
              raw: [],
              objects: [],
              rowCount: 0,
              columnCount: 0,
              error: 'Sheet is empty'
            };
            return;
          }
          
          // Convert sheet to JSON (array format)
          const jsonData = XLSX.utils.sheet_to_json(worksheet, {
            header: 1, // Use array of arrays format
            defval: '', // Default value for empty cells
            raw: false // Convert all values to strings
          });
          
          // Convert sheet to JSON (object format)
          const jsonObjects = XLSX.utils.sheet_to_json(worksheet, {
            defval: '',
            raw: false
          });
          
          allSheetData[sheetName] = {
            raw: jsonData, // Array of arrays (includes headers)
            objects: jsonObjects, // Array of objects (header row becomes keys)
            rowCount: jsonData.length,
            columnCount: jsonData[0]?.length || 0
          };
          
          // console.log(`  ✅ Sheet "${sheetName}" processed successfully`);
          // console.log(`     - Total Rows: ${jsonData.length}`);
          // console.log(`     - Total Columns: ${jsonData[0]?.length || 0}`);
          // console.log(`     - Headers:`, jsonData[0] || []);
          // console.log(`     - Data Rows (excluding header): ${jsonData.length - 1}`);
          
          // Log first few rows
          if (jsonData.length > 0) {
            // console.log(`     - First 3 rows as arrays:`, jsonData.slice(0, 3));
          }
          if (jsonObjects.length > 0) {
            // console.log(`     - First 3 rows as objects:`, jsonObjects.slice(0, 3));
          }
          
        } catch (sheetError) {
          console.error(`❌ Error processing sheet "${sheetName}":`, sheetError);
          allSheetData[sheetName] = {
            raw: [],
            objects: [],
            rowCount: 0,
            columnCount: 0,
            error: sheetError.message
          };
        }
      });
      
      // Step 6: Display complete data
      // console.log('\n📦 ========================================');
      // console.log('📦 Complete Sheet Data (JSON):');
      // console.log(JSON.stringify(allSheetData, null, 2));
      // console.log('📦 ========================================');
      
      // Get first sheet data
      const firstSheetName = workbook.SheetNames[0];
      const sheetData = allSheetData[firstSheetName];
      
      // console.log('\n✅ ========================================');
      // console.log('✅ Excel file read successfully!');
      // console.log('✅ Main Sheet:', firstSheetName);
      // console.log('✅ Total Sheets:', workbook.SheetNames.length);
      // console.log('✅ Main Sheet Data:', sheetData);
      // console.log('✅ ========================================\n');
      
      // Show success message
      const totalRows = sheetData.rowCount - 1; // Exclude header row
      toast.success(
        `Excel file loaded successfully! Found ${workbook.SheetNames.length} sheet(s) with ${totalRows} data rows. Check console for details.`
      );
      
      // You can now use sheetData.objects or sheetData.raw for processing
      // sheetData.objects = Array of objects with headers as keys
      // sheetData.raw = Array of arrays (first row is headers)
      
    } catch (error) {
      console.error('\n❌ ========================================');
      console.error('❌ ERROR READING EXCEL FILE');
      console.error('❌ ========================================');
      console.error('Error Name:', error.name);
      console.error('Error Message:', error.message);
      console.error('Error Stack:', error.stack);
      console.error('File Details:', {
        name: file.name,
        size: file.size,
        type: file.type,
        extension: fileExtension
      });
      console.error('❌ ========================================\n');
      
      // Show detailed error message
      let errorMessage = 'Failed to read Excel file. ';
      
      if (error.message.includes('xlsx library not found')) {
        errorMessage += 'Please install xlsx library: npm install xlsx';
      } else if (error.message.includes('Invalid Excel file format')) {
        errorMessage += 'The file may be corrupted or in an unsupported format.';
      } else if (error.message.includes('no sheets')) {
        errorMessage += 'The Excel file contains no sheets.';
      } else {
        errorMessage += `Error: ${error.message}`;
      }
      
      errorMessage += ' Check console for more details.';
      
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  // Trigger bulk upload modal
  const handleUploadClick = () => {
    if (!permissions.trainee_management_create) {
      toast.warning("You don't have permission to bulk upload trainees");
      return;
    }
    setShowBulkUploadModal(true);
    setBulkUploadStep('upload');
    setBulkUploadFile(null);
    setBulkUploadData([]);
    setBulkUploadErrors([]);
  };

  // Close bulk upload modal
  const handleCloseBulkUploadModal = () => {
    setShowBulkUploadModal(false);
    setBulkUploadFile(null);
    setBulkUploadData([]);
    setBulkUploadErrors([]);
    setBulkUploadSuccesses([]);
    setBulkUploadStep('upload');
  };

  // Download template
  const handleDownloadTemplate = () => {
    const link = document.createElement('a');
    link.href = '/bulk_user_registration_template.csv';
    link.download = 'bulk_user_registration_template.csv';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    // toast.success('Template downloaded successfully');
  };

  // Convert Excel serial date to dd-mm-yyyy format
  const excelDateToString = (serial) => {
    if (!serial) return '';

    // If it's already a string in correct format, return it
    if (typeof serial === 'string' && /^\d{2}-\d{2}-\d{4}$/.test(serial)) {
      return serial;
    }

    // If it's a number (Excel serial date)
    if (typeof serial === 'number') {
      const utc_days = Math.floor(serial - 25569);
      const utc_value = utc_days * 86400;
      const date_info = new Date(utc_value * 1000);

      const day = String(date_info.getUTCDate()).padStart(2, '0');
      const month = String(date_info.getUTCMonth() + 1).padStart(2, '0');
      const year = date_info.getUTCFullYear();

      return `${day}-${month}-${year}`;
    }

    return String(serial);
  };

  // Handle file selection for bulk upload
  const handleBulkFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    const validTypes = [
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'application/vnd.ms-excel',
      'text/csv'
    ];

    if (!validTypes.includes(file.type) && !file.name.match(/\.(xlsx|xls|csv)$/i)) {
      toast.error('Please select only Excel (.xlsx, .xls) or CSV files');
      return;
    }

    setBulkUploadFile(file);
    setBulkUploadLoading(true);
    setBulkUploadStep('validate');

    try {
      const data = await file.arrayBuffer();
      const workbook = XLSX.read(data);
      const worksheet = workbook.Sheets[workbook.SheetNames[0]];
      const jsonData = XLSX.utils.sheet_to_json(worksheet);

      if (jsonData.length === 0) {
        toast.error('Excel file is empty');
        setBulkUploadStep('upload');
        setBulkUploadFile(null);
        return;
      }

      // Validate and transform data
      const errors = [];
      const validatedData = jsonData.map((row, index) => {
        const rowNumber = index + 2;
        const rowErrors = [];

        // Map Excel columns to API format
        // Automatically add "+" to country code if not present
        let countryCode = String(row['Country Code'] || '').trim();
        if (countryCode && !countryCode.startsWith('+')) {
          countryCode = '+' + countryCode;
        }

        const user = {
          name: String(row['Name'] || '').trim(),
          email: String(row['Email'] || '').trim(),
          gender: String(row['Gender'] || '').trim(),
          date_of_birth: excelDateToString(row['Date of Birth']),
          password: String(row['Password'] || ''),
          mobileno_code: countryCode,
          mobile: String(row['Phone Number'] || '').trim(),
          employee_no: String(row['Employee No'] || '').trim(),
          company_code: String(row['Company Code'] || '').trim(),
          company_description: String(row['Company Description'] || '').trim(),
          job_title: String(row['Job Title'] || '').trim(),
          date_joined: excelDateToString(row['Date Joined']),
          ic_no: String(row['IC No'] || '').trim(),
          fin_no: String(row['FIN No'] || '').trim(),
          pwm_rank: String(row['PWM Rank'] || '').trim()
        };

        // Validate mandatory fields
        if (!user.name || user.name.trim() === '') {
          rowErrors.push('Name is required');
        } else if (user.name.length > 25) {
          rowErrors.push('Name must be maximum 25 characters');
        }

        if (!user.email || user.email.trim() === '') {
          rowErrors.push('Email is required');
        } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(user.email)) {
          rowErrors.push('Invalid email format');
        }

        if (!user.gender || !['Male', 'Female', 'Other'].includes(user.gender)) {
          rowErrors.push('Gender must be Male, Female, or Other');
        }

        if (!user.date_of_birth) {
          rowErrors.push('Date of Birth is required');
        } else if (!/^\d{2}-\d{2}-\d{4}$/.test(user.date_of_birth)) {
          rowErrors.push('Date of Birth must be in dd-mm-yyyy format');
        }

        if (!user.password || user.password.trim() === '') {
          rowErrors.push('Password is required');
        } else if (user.password.length < 8) {
          rowErrors.push('Password must be at least 8 characters');
        } else if (!/^(?=.*[a-zA-Z])(?=.*\d)(?=.*[!@#$%^&*(),.?":{}|<>])[A-Za-z\d!@#$%^&*(),.?":{}|<>]{8,}$/.test(user.password)) {
          rowErrors.push('Password must contain at least one letter, one number, and one special character');
        }

        // Validate country code (optional, but if provided must be valid)
        // Note: The code automatically adds "+" so we validate the format after adding it
        if (user.mobileno_code && user.mobileno_code.trim() !== '') {
          if (!/^\+\d{1,4}$/.test(user.mobileno_code)) {
            rowErrors.push('Country Code must be 1-4 digits (e.g., 91, 1, 971, 65)');
          }
        }

        // Validate phone number (optional, but if provided must be valid)
        if (user.mobile && user.mobile.trim() !== '') {
          // Remove any spaces or special characters for validation
          const cleanedPhone = user.mobile.replace(/[\s\-()]/g, '');
          if (!/^\d{5,15}$/.test(cleanedPhone)) {
            rowErrors.push('Phone Number must be 5-15 digits (numbers only)');
          }
        }

        if (rowErrors.length > 0) {
          errors.push({
            row: rowNumber,
            email: user.email || 'N/A',
            errors: rowErrors
          });
        }

        return user;
      });

      setBulkUploadData(validatedData);
      setBulkUploadErrors(errors);

      if (errors.length > 0) {
        toast.warning(`Found ${errors.length} validation error(s). Please review before submitting.`);
      } else {
        toast.success(`Validated ${validatedData.length} user(s) successfully`);
      }

    } catch (error) {
      console.error('Error parsing file:', error);
      toast.error('Error reading file. Please check the file format.');
      setBulkUploadStep('upload');
      setBulkUploadFile(null);
    } finally {
      setBulkUploadLoading(false);
    }
  };

  // Submit bulk upload
  const handleBulkUploadSubmit = async () => {
    if (bulkUploadErrors.length > 0) {
      toast.error('Please fix all validation errors before submitting');
      return;
    }

    if (bulkUploadData.length === 0) {
      toast.error('No data to upload');
      return;
    }

    setBulkUploadLoading(true);
    setBulkUploadStep('submit');

    try {
      const response = await bulkUploadUsersJSON(bulkUploadData);

      // Store both successes and errors from the response
      const successes = response.data?.successes || [];
      const errors = response.data?.errors || [];

      setBulkUploadSuccesses(successes);
      setBulkUploadErrors(errors);

      if (response.success || successes.length > 0) {
        // Show result screen if there are any successes or partial success
        setBulkUploadStep('result');

        // Refresh trainee list
        fetchTraineesData();

        // Show appropriate toast message
        if (errors.length === 0) {
          toast.success(response.message);
        } else if (successes.length > 0) {
          toast.warning(`Partial success: ${successes.length} registered, ${errors.length} failed`);
        } else {
          toast.error(response.message || 'Failed to upload users');
        }

        // Don't auto-close if there are errors - let user review
        if (errors.length === 0) {
          setTimeout(() => {
            handleCloseBulkUploadModal();
          }, 2000);
        }
      } else {
        toast.error(response.message || 'Failed to upload users');
        setBulkUploadStep('validate');
      }
    } catch (error) {
      console.error('Bulk upload error:', error);
      toast.error('Error uploading users. Please try again.');
      setBulkUploadStep('validate');
    } finally {
      setBulkUploadLoading(false);
    }
  };

  // Keep activeTab in sync when embedded with forceTab
  useEffect(() => {
    if (forceTab) setActiveTab(forceTab);
  }, [forceTab]);

  // Use effect for initial data fetch and search
  useEffect(() => {
    if (activeTab !== 'trainees') return;
    fetchTraineesData();
  }, [pagination.page, pagination.limit, activeFilter, debouncedSearchTerm, activeTab]);

  // Filter trainees based on status
  const filteredTrainees = trainees;

  const handleAnalytics = (traineeId) => {
    if (!permissions.trainee_management_view_analytics) {
      toast.warning("You don't have permission to view trainee analytics");
      return;
    }
    const encodedTraineeId = encodeData(traineeId.toString()); // Convert ID to string before encoding
    // Navigate to trainee analytics page with the trainee ID
    navigate(`/admin/traineesAnalytics/${encodedTraineeId}`);
  };

  return (
    <>
      {/* Tabs: Trainees list vs Trainee Groups (hidden when embedded in Access Management) */}
      {!isEmbedded && (
        <div className="mb-4 flex flex-wrap gap-2">
          {[
            { id: 'trainees', label: 'Trainees' },
            { id: 'groups', label: 'Trainee Groups' },
          ].map((tab) => (
            <button
              key={tab.id}
              type="button"
              onClick={() => setActiveTab(tab.id)}
              className={`inline-flex items-center px-3.5 py-2 rounded-full text-sm font-medium border transition-colors ${
                activeTab === tab.id
                  ? 'bg-[#7083FF] text-white border-[#7083FF] shadow-sm'
                  : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>
      )}

      {activeTab === 'trainees' ? (
        <>
      {/* Search, Filter and Actions Toolbar */}
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 mb-6">
        <div className="flex flex-1 flex-col sm:flex-row items-center gap-4 w-full">
          <div className="relative flex-1 w-full group/search">
            <Icon icon="fluent:search-20-regular" className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 group-focus-within/search:text-[#7083FF] transition-colors" />
            <input
              type="search"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search trainees by name or email..."
              className="w-full pl-11 pr-4 py-3 rounded-2xl border border-gray-100 bg-white text-sm shadow-sm focus:outline-none focus:ring-4 focus:ring-[#7083FF]/5 focus:border-[#7083FF]/30 transition-all font-medium"
              autoComplete="off"
            />
          </div>

          <div className="flex items-center gap-2 w-full sm:w-auto">


            <div className="flex-1 sm:flex-none flex items-center gap-2 bg-white p-1 rounded-2xl shadow-sm border border-gray-100 min-w-[140px]">
                <Icon icon="fluent:filter-20-regular" className="ml-3 w-4 h-4 text-gray-400" />
                <select
                  value={activeFilter}
                  onChange={(e) => setActiveFilter(e.target.value)}
                  className="w-full pl-2 pr-8 py-2.5 rounded-xl bg-transparent text-sm font-semibold focus:outline-none cursor-pointer"
                >
                  <option value="all">All Status</option>
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                </select>
            </div>
          </div>
        </div>

        <div className="flex items-center gap-3 w-full lg:w-auto">
            <button
                type="button"
                onClick={handleUploadClick}
                disabled={loading || !permissions.trainee_management_create}
                className="flex-1 lg:flex-none inline-flex items-center justify-center gap-2 px-5 py-3.5 rounded-2xl bg-white border border-gray-100 text-gray-700 text-sm font-bold shadow-sm hover:bg-gray-50 transition-all active:scale-[0.98] disabled:opacity-50"
            >
                <Icon icon="fluent:arrow-upload-24-regular" width="20" height="20" className="text-[#7083FF]" />
                Bulk Upload
            </button>
            <button
                type="button"
                onClick={() => handleShowModal('Create')}
                disabled={loading || !permissions.trainee_management_create}
                className="flex-1 lg:flex-none inline-flex items-center justify-center gap-2.5 px-6 py-3.5 rounded-2xl bg-[#7083FF] text-white text-sm font-bold shadow-xl shadow-[#7083FF]/20 hover:bg-[#7083FF]/90 transition-all hover:scale-[1.02] active:scale-[0.98] disabled:opacity-50"
            >
                <Icon icon="fluent:add-24-regular" width="20" height="20" />
                Add Trainee
            </button>
        </div>
      </div>

      {loading ? (
        <div className="flex flex-col items-center justify-center py-20 bg-white/50 backdrop-blur-sm rounded-3xl border border-gray-100 border-dashed">
            <div className="w-12 h-12 border-4 border-[#7083FF]/20 border-t-[#7083FF] rounded-full animate-spin mb-4" />
            <p className="text-gray-500 font-medium">Loading trainees...</p>
        </div>
      ) : trainees.length === 0 ? (
        <div className="py-20 text-center bg-white rounded-3xl border border-gray-100 border-dashed shadow-sm">
          <div className="w-20 h-20 bg-gray-50 rounded-full flex items-center justify-center mx-auto mb-4">
            <Icon icon="fluent:people-search-24-regular" className="text-gray-300 w-10 h-10" />
          </div>
          <h3 className="text-gray-900 font-bold text-lg">No Trainees Found</h3>
          <p className="text-gray-500 max-w-xs mx-auto mt-2">Try adjusting your search or filters to find what you're looking for.</p>
        </div>
      ) : (
        <>
          <div className="bg-white rounded-[24px] border border-gray-100 shadow-sm overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-left">
                <thead>
                  <tr className="bg-gray-50/50 border-b border-gray-100">
                    <th className="px-6 py-4 text-[11px] font-bold text-gray-400 uppercase tracking-wider">Trainee</th>
                    <th className="px-6 py-4 text-[11px] font-bold text-gray-400 uppercase tracking-wider text-center">Courses</th>
                    <th className="px-6 py-4 text-[11px] font-bold text-gray-400 uppercase tracking-wider text-center">Certificates</th>
                    <th className="px-6 py-4 text-[11px] font-bold text-gray-400 uppercase tracking-wider">Status</th>
                    <th className="px-6 py-4 text-[11px] font-bold text-gray-400 uppercase tracking-wider text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {trainees.map((trainee) => (
                    <tr 
                      key={trainee.id} 
                      className="hover:bg-gray-50/70 transition-colors group cursor-pointer"
                      onClick={(e) => {
                        if (!e.target.closest('.action-btn')) {
                          handleAnalytics(trainee.id);
                        }
                      }}
                    >
                      <td className="px-6 py-5">
                        <div className="flex items-center gap-4">
                          <div className="relative">
                              {trainee.profile_pic_url ? (
                                  <img src={trainee.profile_pic_url} className="w-11 h-11 rounded-2xl object-cover ring-2 ring-white shadow-sm" alt={trainee.name} />
                              ) : (
                                  <div className="w-11 h-11 rounded-2xl bg-gray-100 flex items-center justify-center text-gray-400 border border-gray-200">
                                      <Icon icon="fluent:person-24-regular" width="22" />
                                  </div>
                              )}
                              <div className={`absolute -bottom-0.5 -right-0.5 w-3.5 h-3.5 rounded-full border-2 border-white ${!trainee.is_blocked ? 'bg-green-500' : 'bg-red-500'}`} />
                          </div>
                          <div className="min-w-0">
                            <p className="font-bold text-gray-900 truncate">{trainee.name}</p>
                            <p className="text-xs text-gray-500 truncate">{trainee.email}</p>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-5 text-center">
                        <span className="inline-flex items-center px-2.5 py-1 rounded-lg text-xs font-bold bg-blue-50 text-blue-700">
                          {trainee.active_courses || 0}
                        </span>
                      </td>
                      <td className="px-6 py-5 text-center">
                        <span className="inline-flex items-center px-2.5 py-1 rounded-lg text-xs font-bold bg-amber-50 text-amber-700">
                          {trainee.certificate_count || 0}
                        </span>
                      </td>
                      <td className="px-6 py-5">
                        <button
                          onClick={(e) => { e.stopPropagation(); handleStatusToggle(trainee); }}
                          disabled={!permissions.trainee_management_status}
                          className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-bold transition-all action-btn ${
                              !trainee.is_blocked
                              ? 'text-green-600 bg-green-50 hover:bg-green-100 ring-1 ring-green-200/50'
                              : 'text-red-600 bg-red-50 hover:bg-red-100 ring-1 ring-red-200/50'
                          }`}
                        >
                          <Icon icon={!trainee.is_blocked ? "fluent:checkmark-circle-24-regular" : "fluent:error-circle-24-regular"} />
                          {!trainee.is_blocked ? 'Active' : 'Blocked'}
                        </button>
                      </td>
                      <td className="px-6 py-5 text-right">
                        <div className="flex items-center justify-end gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                          <button
                            onClick={(e) => { e.stopPropagation(); handleShowModal('Edit', trainee); }}
                            disabled={!permissions.trainee_management_edit}
                            className="p-2.5 rounded-xl text-gray-500 hover:text-[#7083FF] hover:bg-[#7083FF]/5 transition-all action-btn"
                            title="Edit Trainee"
                          >
                            <Icon icon="fluent:edit-24-regular" width="20" />
                          </button>
                          <button
                            onClick={(e) => { e.stopPropagation(); handleAnalytics(trainee.id); }}
                            disabled={!permissions.trainee_management_view_analytics}
                            className="p-2.5 rounded-xl text-gray-500 hover:text-blue-600 hover:bg-blue-50 transition-all action-btn"
                            title="View Analytics"
                          >
                            <Icon icon="fluent:data-trending-24-regular" width="20" />
                          </button>
                          <button
                            onClick={(e) => { e.stopPropagation(); handleDelete(trainee); }}
                            disabled={!permissions.trainee_management_delete}
                            className="p-2.5 rounded-xl text-gray-500 hover:text-red-600 hover:bg-red-50 transition-all action-btn"
                            title="Delete Trainee"
                          >
                            <Icon icon="fluent:delete-24-regular" width="20" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Pagination */}
          {/* Pagination Hub */}
          {pagination.totalCount > 0 && (
            <div className="mt-8 flex flex-col sm:flex-row items-center justify-between gap-4 p-4 bg-gray-50/50 rounded-[24px] border border-gray-100">
              <div className="flex items-center gap-3">
                  <p className="text-sm font-medium text-gray-500">
                    Showing <span className="text-gray-900 font-bold">{trainees.length}</span> of <span className="text-gray-900 font-bold">{pagination.totalCount}</span> trainees
                  </p>
                  <div className="h-4 w-px bg-gray-200 hidden sm:block" />
                  <div className="flex items-center gap-2">
                      <span className="text-[11px] font-bold text-gray-400 uppercase tracking-wider">Rows:</span>
                      <select
                        className="bg-transparent text-sm font-bold text-gray-700 focus:outline-none cursor-pointer"
                        value={pagination.limit}
                        onChange={(e) => setPagination(prev => ({ ...prev, limit: Number(e.target.value), page: 1 }))}
                      >
                        <option value={10}>10</option>
                        <option value={20}>20</option>
                        <option value={50}>50</option>
                      </select>
                  </div>
              </div>

              {pagination.totalPages > 1 && (
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => handlePageChange(pagination.page - 1)}
                    disabled={pagination.page === 1 || loading}
                    className="p-2 rounded-xl bg-white border border-gray-100 text-gray-500 hover:text-primary disabled:opacity-50 disabled:cursor-not-allowed shadow-sm transition-all"
                  >
                    <Icon icon="fluent:chevron-left-24-regular" width="20" />
                  </button>
                  <div className="flex items-center px-4 py-2 bg-white rounded-xl border border-gray-100 shadow-sm">
                    <span className="text-sm font-bold text-primary">{pagination.page}</span>
                    <span className="text-sm font-medium text-gray-300 mx-1">/</span>
                    <span className="text-sm font-medium text-gray-500">{pagination.totalPages}</span>
                  </div>
                  <button
                    onClick={() => handlePageChange(pagination.page + 1)}
                    disabled={pagination.page >= pagination.totalPages || loading}
                    className="p-2 rounded-xl bg-white border border-gray-100 text-gray-500 hover:text-primary disabled:opacity-50 disabled:cursor-not-allowed shadow-sm transition-all"
                  >
                    <Icon icon="fluent:chevron-right-24-regular" width="20" />
                  </button>
                </div>
              )}
            </div>
          )}
        </>
      )}

      {/* Add/Edit Trainee Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4" onClick={handleCloseModal}>
          <div className="bg-white rounded-2xl shadow-2xl max-w-xl w-full max-h-[88vh] flex flex-col" onClick={e => e.stopPropagation()}>
            <div className="px-5 py-3 border-b border-gray-100 flex justify-between items-center bg-white rounded-t-2xl flex-shrink-0">
              <h5 className="font-semibold text-lg text-gray-900">{mode === 'Create' ? 'Add Trainee' : 'Edit Trainee'}</h5>
              <button type="button" className="w-9 h-9 inline-flex items-center justify-center rounded-xl hover:bg-gray-100 text-gray-500" onClick={handleCloseModal} aria-label="Close">
                <Icon icon="mdi:close" width="20" height="20" />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto p-5">
            <form className="space-y-3">
              {/* Profile Image Upload */}
              <div className="text-center">
                <div className="relative inline-block">
                  <div className="w-20 h-20 sm:w-24 sm:h-24 rounded-full overflow-hidden border-2 border-dashed border-gray-300 bg-gray-50 flex items-center justify-center">
                    {imagePreview ? (
                      <img
                        src={imagePreview}
                        alt="Profile Preview"
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="text-center p-3">
                        <Icon
                          icon="fluent:person-24-regular"
                          width="28"
                          height="28"
                          className="text-gray-400 mb-2 mx-auto"
                        />
                        <div className="text-gray-500 text-sm font-medium">
                          Upload Photo
                        </div>
                      </div>
                    )}
                  </div>
                  <input
                    type="file"
                    accept="image/*"
                    className="absolute inset-0 opacity-0 cursor-pointer"
                    onChange={handleImageChange}
                  />
                  {imagePreview && (
                    <button
                      type="button"
                      className="bg-red-500 hover:bg-red-600 text-white font-medium rounded-full absolute w-7 h-7 flex items-center justify-center"
                      onClick={() => {
                        setImagePreview(null);
                        setFormData(prev => ({
                          ...prev,
                          profileImage: null
                        }));
                      }}
                      style={{ top: '4px', right: '4px', border: '2px solid #fff', boxShadow: '0 2px 4px rgba(0,0,0,0.2)' }}
                    >
                      <Icon icon="fluent:dismiss-12-filled" width="12" height="12" color="#fff" />
                    </button>
                  )}
                </div>
                <div className="text-gray-500 mt-2 text-sm">
                  Click to upload profile photo
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">
                    Name<span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    className={`w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#7083FF]/15 focus:border-[#7083FF]/40 bg-gray-50 ${errors.name ? 'border-red-500' : ''}`}
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    maxLength={25}
                    required
                  />
                  {errors.name && <div className="text-red-500 text-sm mt-1">{errors.name}</div>}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">
                    Email<span className="text-red-500">*</span>
                  </label>
                  <input
                    type="email"
                    className={`w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#7083FF]/15 focus:border-[#7083FF]/40 bg-gray-50 ${errors.email ? 'border-red-500' : ''} ${mode === 'Edit' ? 'cursor-not-allowed' : ''}`}
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    placeholder="lms.admin@gmail.com"
                    required
                    readOnly={mode === 'Edit'}
                    style={mode === 'Edit' ? { backgroundColor: '#e9ecef' } : {}}
                  />
                  {errors.email && <div className="text-red-500 text-sm mt-1">{errors.email}</div>}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">
                    Gender<span className="text-red-500">*</span>
                  </label>
                  <select
                    className={`w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#7083FF]/15 focus:border-[#7083FF]/40 bg-gray-50 ${errors.gender ? 'border-red-500' : ''}`}
                    name="gender"
                    value={formData.gender}
                    onChange={handleInputChange}
                    required
                  >
                    <option value="">Select Gender</option>
                    <option value="male">Male</option>
                    <option value="female">Female</option>
                    <option value="other">Other</option>
                  </select>
                  {errors.gender && <div className="text-red-500 text-sm mt-1">{errors.gender}</div>}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">
                    Date of Birth<span className="text-red-500">*</span>
                  </label>
                  <input
                    type="date"
                    className={`w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#7083FF]/15 focus:border-[#7083FF]/40 bg-gray-50 ${errors.dateOfBirth ? 'border-red-500' : ''}`}
                    name="dateOfBirth"
                    value={formData.dateOfBirth}
                    onChange={handleInputChange}
                    required
                  />
                  {errors.dateOfBirth && <div className="text-red-500 text-sm mt-1">{errors.dateOfBirth}</div>}
                </div>

                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Phone Number</label>
                  <div className="grid grid-cols-12 gap-2">
                    <div className="col-span-12 md:col-span-4">
                      <select
                        className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#7083FF]/15 focus:border-[#7083FF]/40 bg-gray-50"
                        name="countryCode"
                        value={formData.countryCode}
                        onChange={handleInputChange}
                      >
                        {countryData.countoryCode.map((country, index) => (
                          <option key={index} value={country.code}>
                            {country.country}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="col-span-12 md:col-span-8">
                      <input
                        type="text"
                        className={`w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#7083FF]/15 focus:border-[#7083FF]/40 bg-gray-50 ${errors.phoneNumber ? 'border-red-500' : ''}`}
                        name="phoneNumber"
                        value={formData.phoneNumber}
                        onChange={handleInputChange}
                        placeholder="Enter phone number"
                      />
                      {errors.phoneNumber && <div className="text-red-500 text-sm mt-1">{errors.phoneNumber}</div>}
                    </div>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Employee No</label>
                  <input
                    type="text"
                    className={`w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#7083FF]/15 focus:border-[#7083FF]/40 bg-gray-50 ${errors.employeeNo ? 'border-red-500' : ''}`}
                    name="employeeNo"
                    value={formData.employeeNo}
                    onChange={handleInputChange}
                    maxLength={50}
                  />
                  {errors.employeeNo && <div className="text-red-500 text-sm mt-1">{errors.employeeNo}</div>}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Job Title</label>
                  <input
                    type="text"
                    className={`w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#7083FF]/15 focus:border-[#7083FF]/40 bg-gray-50 ${errors.jobTitle ? 'border-red-500' : ''}`}
                    name="jobTitle"
                    value={formData.jobTitle}
                    onChange={handleInputChange}
                    maxLength={1000}
                  />
                  {errors.jobTitle && <div className="text-red-500 text-sm mt-1">{errors.jobTitle}</div>}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Date Joined</label>
                  <input
                    type="date"
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#7083FF]/15 focus:border-[#7083FF]/40 bg-gray-50"
                    name="dateJoined"
                    value={formData.dateJoined}
                    onChange={handleInputChange}
                    placeholder="dd-mm-yyyy"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">
                    Password{mode === 'Create' && <span className="text-red-500">*</span>}
                    {mode === 'Edit' && <span className="text-gray-500"> (Optional)</span>}
                  </label>
                  <div className="relative">
                    <input
                      type={showPassword ? "text" : "password"}
                      className={`w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#7083FF]/15 focus:border-[#7083FF]/40 bg-gray-50 pr-10 ${errors.password ? 'border-red-500' : ''}`}
                      name="password"
                      value={formData.password}
                      onChange={handleInputChange}
                      required={mode === 'Create'}
                      minLength={8}
                      placeholder="Password"
                    />
                    <Icon
                      icon={showPassword ? "fluent:eye-off-24-regular" : "fluent:eye-24-regular"}
                      className="absolute top-1/2 right-0 transform -translate-y-1/2 mr-3 text-gray-500"
                      style={{ cursor: 'pointer' }}
                      onClick={() => setShowPassword(!showPassword)}
                      width="20"
                      height="20"
                    />
                  </div>
                  {errors.password && <div className="text-red-500 text-sm mt-1">{errors.password}</div>}
                </div>
              </div>
            </form>
          </div>
          {/* Buttons */}
          <div className="px-5 py-3 border-t bg-white flex gap-3 rounded-b-2xl flex-shrink-0">
            <button
              type="button"
              className="bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium py-2 px-4 rounded-lg w-full"
              onClick={handleCloseModal}
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="button"
              className="bg-primary hover:bg-primary/90 text-white font-medium py-2 px-4 rounded-lg w-full"
              onClick={handleSave}
              disabled={loading}
            >
              {loading ? (
                <>
                  <span className="inline-block w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" role="status" aria-hidden="true"></span>
                  {mode === 'Create' ? 'Creating...' : 'Updating...'}
                </>
              ) : (
                mode === 'Create' ? 'Add Trainee' : 'Update Trainee'
              )}
            </button>
          </div>
          </div>
        </div>
      )}

      {/* Bulk Upload Modal */}
      {showBulkUploadModal && (
        <div className="fixed inset-0 flex items-center justify-center z-50" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }} onClick={handleCloseBulkUploadModal}>
          <div className="bg-white rounded-xl shadow-xl max-w-4xl w-full mx-4 max-h-[90vh] flex flex-col" onClick={e => e.stopPropagation()}>
            <div className="px-6 py-4 border-b border-gray-100 flex justify-between items-center">
              <h5 className="font-semibold text-lg">Bulk Upload Trainees</h5>
              <button type="button" className="p-1 rounded hover:bg-gray-100" onClick={handleCloseBulkUploadModal} aria-label="Close">
                <Icon icon="mdi:close" width="20" height="20" />
              </button>
            </div>
            <div className="p-6 overflow-y-auto flex-1">
          {bulkUploadStep === 'upload' && (
            <div className="text-center py-4">
              <Icon icon="fluent:document-arrow-up-24-regular" width="64" height="64" className="text-primary mb-3" />
              <h5 className="mb-3">Upload Excel File</h5>
              <p className="text-gray-500 mb-4">
                Upload an Excel file (.xlsx, .xls) or CSV file with trainee information
              </p>

              <div className="mb-4">
                <button
                  type="button"
                  className="border border-primary text-primary hover:bg-primary/10 font-medium py-2 px-4 rounded-lg mr-2 mb-2 inline-flex items-center gap-2"
                  onClick={handleDownloadTemplate}
                >
                  <Icon icon="fluent:arrow-download-24-regular" width="20" height="20" />
                  Download Template
                </button>
                <button
                  type="button"
                  className="bg-primary hover:bg-primary/90 text-white font-medium py-2 px-4 rounded-lg inline-flex items-center gap-2"
                  onClick={() => bulkFileInputRef.current?.click()}
                >
                  <Icon icon="fluent:arrow-upload-24-regular" width="20" height="20" />
                  Select File
                </button>
                <input
                  ref={bulkFileInputRef}
                  type="file"
                  accept=".xlsx,.xls,.csv"
                  onChange={handleBulkFileSelect}
                  className="hidden"
                />
              </div>

              <div className="bg-blue-50 border border-blue-200 text-blue-800 p-4 rounded-lg text-left">
                <h6 className="font-semibold mb-2">Required Columns:</h6>
                <ul className="mb-0 text-sm">
                  <li><strong>Name*</strong> (max 25 characters)</li>
                  <li><strong>Email*</strong> (valid email format)</li>
                  <li><strong>Gender*</strong> (Male, Female, or Other)</li>
                  <li><strong>Date of Birth*</strong> (format: dd-mm-yyyy)</li>
                  <li><strong>Password*</strong> (min 8 chars, must include letter, number, special char)</li>
                  <li><strong>Country Code</strong> (optional, enter numbers only: 91, 65, 1, 971 - defaults to 91 if not provided)</li>
                  <li><strong>Phone Number</strong> (optional, 5-15 digits without country code)</li>
                  <li>Employee No, Company Code, Company Description, Job Title, Date Joined, IC No, FIN No, PWM Rank (all optional)</li>
                </ul>
              </div>
            </div>
          )}

          {bulkUploadStep === 'validate' && (
            <div>
              <div className="flex justify-between items-center mb-3">
                <h6 className="mb-0 flex items-center gap-2">
                  <Icon icon="fluent:checkmark-circle-24-regular" width="24" height="24" className="text-green-600" />
                  File Loaded: {bulkUploadFile?.name}
                </h6>
              </div>
              <button
                type="button"
                className="bg-primary hover:bg-primary/90 text-white text-sm font-medium py-2 px-3 rounded-lg mb-2"
                onClick={() => {
                  setBulkUploadStep('upload');
                  setBulkUploadFile(null);
                  setBulkUploadData([]);
                  setBulkUploadErrors([]);
                }}
              >
                Change File
              </button>

              <div className="bg-blue-50 border border-blue-200 text-blue-800 p-3 rounded-lg mb-3">
                <strong>Total Users:</strong> {bulkUploadData.length} |
                <strong className="ml-2">Valid:</strong> {bulkUploadData.length - bulkUploadErrors.length} |
                <strong className="ml-2 text-red-600">Errors:</strong> {bulkUploadErrors.length}
              </div>

              {bulkUploadErrors.length > 0 && (
                <div className="mb-3 max-h-[300px] overflow-y-auto">
                  <h6 className="text-red-600 mb-2">Validation Errors:</h6>
                  {bulkUploadErrors.map((error, index) => (
                    <div key={index} className="bg-red-50 border border-red-200 text-red-800 py-2 px-3 rounded text-sm mb-2">
                      <strong>Row {error.row}</strong> ({error.email}):
                      <ul className="mb-0 mt-1 list-disc list-inside">
                        {error.errors ? (
                          error.errors.map((err, i) => (
                            <li key={i}>{err}</li>
                          ))
                        ) : (
                          <li>{error.error}</li>
                        )}
                      </ul>
                    </div>
                  ))}
                </div>
              )}

              {bulkUploadErrors.length === 0 && (
                <div className="bg-green-50 border border-green-200 text-green-800 p-3 rounded-lg flex items-center gap-2">
                  <Icon icon="fluent:checkmark-circle-24-filled" width="20" height="20" />
                  All users validated successfully! Ready to upload.
                </div>
              )}
            </div>
          )}

          {bulkUploadStep === 'submit' && (
            <div className="text-center py-5">
              <div className="inline-block w-10 h-10 border-2 border-primary border-t-transparent rounded-full animate-spin mb-3" role="status" aria-hidden="true" />
              <h5>Uploading users...</h5>
              <p className="text-gray-500">Please wait while we process your request</p>
            </div>
          )}

          {bulkUploadStep === 'result' && (
            <div className="py-4">
              {/* Success Section */}
              {bulkUploadSuccesses.length > 0 && (
                <div className="mb-4">
                  <div className="flex items-center mb-3">
                    <Icon icon="fluent:checkmark-circle-24-filled" width="32" height="32" className="text-green-600 mr-2" />
                    <h5 className="text-green-600 mb-0">
                      {bulkUploadSuccesses.length} user{bulkUploadSuccesses.length > 1 ? 's' : ''} registered successfully
                    </h5>
                  </div>
                  <div className="bg-green-50 border border-green-200 text-green-800 p-3 rounded-lg">
                    <ul className="mb-0 list-disc list-inside">
                      {bulkUploadSuccesses.map((success, index) => (
                        <li key={index}>
                          <strong>{success.email}</strong> - {success.name}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              )}

              {/* Error Section */}
              {bulkUploadErrors.length > 0 && (
                <div className="mb-4">
                  <div className="flex items-center mb-3">
                    <Icon icon="fluent:error-circle-24-filled" width="32" height="32" className="text-red-600 mr-2" />
                    <h5 className="text-red-600 mb-0">
                      {bulkUploadErrors.length} user{bulkUploadErrors.length > 1 ? 's' : ''} failed
                    </h5>
                  </div>
                  <div className="bg-red-50 border border-red-200 text-red-800 p-3 rounded-lg">
                    <ul className="mb-0 list-disc list-inside">
                      {bulkUploadErrors.map((error, index) => (
                        <li key={index}>
                          <strong>{error.email}</strong> - {error.error}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              )}

              {/* Summary Message */}
              {bulkUploadSuccesses.length === 0 && bulkUploadErrors.length === 0 && (
                <div className="text-center">
                  <Icon icon="fluent:info-24-regular" width="64" height="64" className="text-gray-500 mb-3" />
                  <h5 className="text-gray-500">No results to display</h5>
                </div>
              )}
            </div>
          )}
            </div>
            <div className="px-6 py-4 border-t border-gray-100 flex justify-end gap-3">
              <button
                type="button"
                className="bg-gray-100 hover:bg-gray-200 text-gray-800 font-medium py-2 px-4 rounded-lg"
                onClick={handleCloseBulkUploadModal}
                disabled={bulkUploadLoading}
              >
                {bulkUploadStep === 'result' ? 'Close' : 'Cancel'}
              </button>
              {bulkUploadStep === 'validate' && (
                <button
                  type="button"
                  className="bg-primary hover:bg-primary/90 text-white font-medium py-2 px-4 rounded-lg inline-flex items-center gap-2"
                  onClick={handleBulkUploadSubmit}
                  disabled={bulkUploadLoading || bulkUploadErrors.length > 0}
                >
                  <Icon icon="fluent:arrow-upload-24-regular" width="20" height="20" />
                  Upload {bulkUploadData.length} User(s)
                </button>
              )}
            </div>
          </div>
        </div>
      )}

        </>
      ) : (
        <TraineeGroups />
      )}
    </>
  );
}

export default Trainees;
